import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import { walletKeys } from './wallet';
import { petSlotsKeys } from './petSlots';
// Type-only, circular with care.ts (which imports CompanionDto back for its own cache-walk
// typing) — safe because `import type` is fully erased at compile time (same pattern looks.ts
// <-> profiles.ts already uses).
import type { CareDto } from './care';
import type { Outfit } from '../pet3d/types';

// Phase 3 widening (pets/CompanionDto.java): the server's `stats` field is a
// `Map<String, Integer>`, but StatService.allocate/of() always populate exactly these seven keys
// (StatService.STATS) — typed as a closed shape (not Record<string, number>) since every consumer
// reads exactly these. `ward` (combat-rebuild Task 1's own `companions.ward` column, Task 20 fix
// round: this type had drifted from the wire — CompanionDto.of already put it in the map) is the
// companion's magical-defense stat, alongside guard's physical one.
export type StatBlock = {
  vitality: number;
  power: number;
  focus: number;
  guard: number;
  ward: number;
  speed: number;
  fortune: number;
};

// Task 9 (2026-08-23 battle-gore-and-wash): server pets/Stain.java. Cosmetic only. `x`/`y` are
// normalised into the sprite's own square box, so one record renders at every size the pet is
// drawn at. Field names match the Java record verbatim: seed, x, y, scale, rot, tone.
// Server: pets/StainAnchor.java (3D characters battle, spec §4.3) — absent on legacy stains.
export type StainAnchorDto = {
  stage: 'hatchling' | 'juvenile' | 'adult';
  part: string;
  p: [number, number, number];
  n: [number, number, number];
  front: boolean;
  /** Spec §6 (W6): the worn piece the drop hit; the stain shows only while that item is worn. */
  item?: string;
};

export type StainDto = {
  seed: number;
  x: number;
  y: number;
  scale: number;
  rot: number;
  tone: number;
  anchor?: StainAnchorDto;
};

export type CompanionDto = {
  id: number;
  name: string;
  speciesCode: string;
  speciesName: string;
  stage: string;
  level: number;
  // Phase 3 (progression/XpService.java, StatService.java): xp/xpToNext drive the level-up
  // progress bar; freePoints is how many unspent allocation points this companion is carrying.
  xp: number;
  xpToNext: number;
  freePoints: number;
  stats: StatBlock;
  isActive: boolean;
  // Task 15: server's CompanionDto.of() now always attaches the decayed-to-now care state
  // (hunger/joy/grooming/thriving/playReadyAt) — see care.ts.
  care: CareDto;
  // Task 9: this companion's permanent battle stains, most-recently-appended last, capped at
  // StainService.MAX_STAINS (12, oldest evicted first). Always an array (never omitted), empty
  // for a companion that has never landed a PvE killing blow. Rendering is a later task.
  stains: StainDto[];
  /** W5: what this companion wears, by slot (transmog projections included); `{}` when nothing is. */
  outfit: Outfit;
};

export const companionsKeys = {
  all: ['companions'] as const,
};

export const listCompanions = () => apiFetch<CompanionDto[]>('/companions');

export const createCompanion = (input: { speciesCode: string; name: string }) =>
  apiFetch<CompanionDto>('/companions', { method: 'POST', body: JSON.stringify(input) });

export const setActiveCompanion = (id: number) =>
  apiFetch<CompanionDto>(`/companions/${id}/active`, { method: 'PUT' });

/** `enabled` (Studio Task 11 fix round 2): `MeScreen`'s logout mutation needs to disarm this
 *  observer for the duration of the request — see its own comment on why. Every other call site
 *  keeps the default. */
export function useCompanions(enabled = true) {
  return useQuery({ queryKey: companionsKeys.all, queryFn: listCompanions, enabled });
}

export function useCreateCompanion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCompanion,
    // Task 19 e2e fix: creating the first Companion also runs StarterGrantService's atomic grant
    // (500 Shells + starter dyes/cosmetics/berries, server/src/main/java/com/projectpets/items/
    // StarterGrantService.java) — the wallet HUD (ShellsHud.tsx) is mounted continuously from the
    // moment registration lands on /habitat, well BEFORE any companion exists, so its query is
    // already cached (at "0 Shells") by the time this mutation succeeds. Without this
    // invalidation the HUD kept showing that stale pre-grant balance indefinitely (nothing else
    // ever re-triggers a wallet fetch on this navigation path) — found by phase2-economy.spec.ts's
    // very first assertion, a real player-visible defect, not a test artifact.
    //
    // Phase 4 Task 15 fix: creating a Companion also consumes one of the account's pet-slot
    // `used` count (PetSlotService, GET /companions/slots) — SlotsRow's own usePetSlots() query
    // had nothing invalidating it on this path (flagged in petSlots.ts's own header comment as
    // "Task 15's slot-UI screen work"), so a fresh companion never reflected in "Companions N/6"
    // until something else happened to refetch it.
    onSuccess: (created) => {
      // Fix sweep 2026-09-19: SEED the list from the response before anything navigates. The
      // pre-creation Habitat visit caches `[]`, and the screen renders the empty den off that
      // cached list until the invalidation's refetch lands — which the e2e battery saw take
      // longer than its 5 s assertion once in four `ability-lesson` runs. The response is the
      // companion; writing it in makes the Habitat's first render correct synchronously, and the
      // invalidation below is then a refresh (stage/active flags the server may have adjusted),
      // not the only way the new pet reaches the screen.
      queryClient.setQueryData<CompanionDto[]>(companionsKeys.all, (old) =>
        old ? [...old.filter((c) => c.id !== created.id), created] : [created]);
      queryClient.invalidateQueries({ queryKey: companionsKeys.all });
      queryClient.invalidateQueries({ queryKey: walletKeys.all });
      queryClient.invalidateQueries({ queryKey: petSlotsKeys.all });
    },
  });
}

export function useSetActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => setActiveCompanion(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: companionsKeys.all }),
  });
}

export type AllocateInput = {
  stat: keyof StatBlock;
  points: number;
};

export const allocate = (companionId: number, input: AllocateInput) =>
  apiFetch<CompanionDto>(`/companions/${companionId}/allocate`, { method: 'POST', body: JSON.stringify(input) });

/**
 * Optimistic against the `companions` list cache (mirrors useCareAction's onMutate/onError/
 * onSettled shape) — allowed here, unlike training/respec/purchase, because allocation spends no
 * currency: the worst a rollback undoes is a guessed stat number, not money. On success the
 * server's own AllocationInvalidException (stat maxed, not enough freePoints, etc.) is a real
 * rejection this optimistic guess can be wrong about, hence the rollback in onError.
 */
export function useAllocate(companionId: number) {
  const queryClient = useQueryClient();
  const key = companionsKeys.all;
  return useMutation({
    mutationFn: (input: AllocateInput) => allocate(companionId, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CompanionDto[]>(key);
      if (previous) {
        queryClient.setQueryData<CompanionDto[]>(
          key,
          previous.map((c) =>
            c.id === companionId
              ? {
                  ...c,
                  freePoints: c.freePoints - input.points,
                  stats: { ...c.stats, [input.stat]: c.stats[input.stat] + input.points },
                }
              : c),
        );
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

export const respec = (companionId: number) =>
  apiFetch<CompanionDto>(`/companions/${companionId}/respec`, {
    method: 'POST',
    body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
  });

// NOT optimistic — a respec costs Shells (currency-gated, same class as usePurchase/
// useStartTraining): no onMutate, settle on the server response. Invalidates companions (the
// reset stats/freePoints) and wallet (Shells spent).
export function useRespec(companionId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => respec(companionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companionsKeys.all });
      queryClient.invalidateQueries({ queryKey: walletKeys.all });
    },
  });
}
